// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 41-groups-management.md

import type { UserListItem } from '@greenlight/shared';

export type UserStatusFilter = 'all' | 'active' | 'deactivated';

/**
 * Narrow an arbitrary URL query value to a known status filter, defaulting to
 * 'all'. Guards against stale bookmarks / typos (e.g. ?status=inactive) that
 * would otherwise leave the list and the filter pills out of agreement.
 */
export function parseUserStatus(raw: string | null | undefined): UserStatusFilter {
  return raw === 'active' || raw === 'deactivated' ? raw : 'all';
}

/**
 * Filter users by activation status and a freetext query matched against
 * display_name (falling back to email) and email.
 */
export function applyUserFilters(
  items: readonly UserListItem[],
  status: UserStatusFilter,
  query: string,
): UserListItem[] {
  const q = query.trim().toLowerCase();
  return items.filter((u) => {
    if (status === 'active' && !u.is_active) return false;
    if (status === 'deactivated' && u.is_active) return false;
    if (!q) return true;
    const nameTarget = (u.display_name ?? u.email).toLowerCase();
    const emailTarget = u.email.toLowerCase();
    return nameTarget.includes(q) || emailTarget.includes(q);
  });
}

export interface UserSections {
  admins: UserListItem[];
  members: UserListItem[];
  deactivated: UserListItem[];
}

/**
 * Partition users into admins, members, and deactivated for sectioned rendering.
 * Admins: active org_admin. Members: active non-admin. Deactivated: inactive.
 */
export function splitUserSections(items: readonly UserListItem[]): UserSections {
  const admins: UserListItem[] = [];
  const members: UserListItem[] = [];
  const deactivated: UserListItem[] = [];
  for (const u of items) {
    if (!u.is_active) {
      deactivated.push(u);
    } else if (u.role === 'org_admin') {
      admins.push(u);
    } else {
      members.push(u);
    }
  }
  return { admins, members, deactivated };
}

export interface UserStatusCounts {
  all: number;
  active: number;
  deactivated: number;
}

/** Count users by activation status for filter pill labels. */
export function userStatusCounts(items: readonly UserListItem[]): UserStatusCounts {
  let active = 0;
  let deactivated = 0;
  for (const u of items) {
    if (u.is_active) {
      active += 1;
    } else {
      deactivated += 1;
    }
  }
  return { all: items.length, active, deactivated };
}
